import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import { cropImageUrl } from '@tock/images';
import { defaultMemoize } from 'reselect';
import { displayLocation } from '@tock/shared/utils/locationUtils';
import { oc } from 'ts-optchain';
import { toTitleCase } from '@tock/shared/utils/string';
import _ from 'lodash';
import protos from 'utils/protos';

const { PaymentProcessorType } = protos;

export function isBraintreeBusiness(business: consumer.IBusinessConfig): boolean {
  return safeEnumCompare(
    business.paymentProcessorType,
    PaymentProcessorType,
    PaymentProcessorType.BRAINTREE
  );
}

export const getAssociatedBusinesses = defaultMemoize(
  (business: consumer.IBusinessConfig, limit = 3): consumer.IRelatedBusiness[] => {
    const inSameState =
      business.businessGroup?.business?.filter((other) => other.state === business.state) || [];
    const others =
      business.businessGroup?.business?.filter((other) => other.state !== business.state) || [];
    return _.take([...inSameState, ...others], limit);
  }
);

export const getNextRelease = defaultMemoize((config: consumer.IConsumerConfig | undefined) => {
  const releases = config?.release || [];
  if (
    releases.length > 0 &&
    (releases[0].openUntil || '') > (config?.business?.ticketsAvailableUntil || '')
  ) {
    return releases[0];
  }

  return null;
});

const normalizeAltText = (altText: string) => {
  const tokens = _.split(altText, ', ');
  if (tokens.length <= 1) {
    return altText || '';
  }
  const lastElement = tokens.pop();
  const lastElementString = lastElement ? `, and ${lastElement}` : '';
  return _.join(tokens, ', ') + lastElementString;
};

export const getAltTextWithFallback = (
  altText: string | undefined | null,
  business: { name?: string | null; city?: string | null },
  experience?: { name?: string }
) => {
  if (!experience) {
    if (altText) {
      return `Image of ${business.name} showing ${normalizeAltText(altText)}`;
    }
    return `Image of ${business.name}`;
  }
  if (altText) {
    return `Image of ${experience.name} showing ${normalizeAltText(altText)}`;
  }
  return `Image of ${experience.name} at ${business.name}`;
};

/*
 * A function that takes a list of business services combines them
 * to create a concise list of objects that represent the operating hours
 * of that business.
 *
 * @param {Service[]} services An array of Service objects.
 * @param {string} weekday An all caps string of a weekday to filter services by.
 * @returns {object[]} A list of the minimum quantiy of time ranges representing
 *     the business' operating hours.
 */
export function getCoalescedRangesFromServices(services: core.Service[], weekday?: string) {
  const servicesOnDay = _.map(
    _.sortBy(
      // If weekday is undefined use all services provided.
      _.filter(services, (hours) => _.isUndefined(weekday) || hours.weekday === weekday),
      ['startTime', 'endTime']
    ),
    (hours) => ({
      openingTime: hours.startTime,
      closingTime: hours.endTime,
    })
  );

  const coalescedServicesOnDay = [];
  let currentTimeRange;
  for (let i = 0; i < servicesOnDay.length; i++) {
    if (!currentTimeRange) {
      currentTimeRange = { ...servicesOnDay[i] };
    } else if (currentTimeRange.closingTime >= servicesOnDay[i].openingTime) {
      currentTimeRange.closingTime = servicesOnDay[i].closingTime;
    } else {
      coalescedServicesOnDay.push(currentTimeRange);
      currentTimeRange = { ...servicesOnDay[i] };
    }
  }
  if (currentTimeRange) {
    coalescedServicesOnDay.push(currentTimeRange);
  }
  return coalescedServicesOnDay;
}

export const getLocality = (
  business: consumer.OfferingBusiness | consumer.BusinessConfig | consumer.IBusinessConfig
) => {
  return displayLocation({
    city: business.city,
    state: business.state,
    country: business.country,
  });
};

export interface BusinessSubtitleExcludeOptions {
  cuisine?: boolean;
  locality?: boolean;
  neighborhood?: boolean;
  priceRange?: boolean;
}

export interface SubtitleOptions {
  excludes?: BusinessSubtitleExcludeOptions;
}

export const getBusinessSubtitle = (
  business: consumer.OfferingBusiness,
  options: SubtitleOptions
) => {
  const locality = getLocality(business);
  const { neighborhood, cuisines, priceRange } = business;
  const presentOptions = [
    options?.excludes?.locality ? null : locality,
    options?.excludes?.neighborhood ? null : neighborhood,
    options?.excludes?.priceRange ? null : priceRange,
    options?.excludes?.cuisine ? null : cuisines,
  ].filter(Boolean);
  return presentOptions.join(' · ');
};

export interface SizeOptions {
  width: number;
  height: number;
}

export const getBusinessThumbnailSrc = (
  business: consumer.IBusinessConfig | consumer.OfferingBusiness,
  options: SizeOptions = { width: 120, height: 120 }
): string => {
  if (!business) {
    return '';
  }
  const imageLink = _.first(_.map(oc(business).profileImage([]), (im) => im.imageUrl));
  return imageLink ? cropImageUrl(imageLink, options.width, options.height) : '';
};

/*
 * Utility to return a business concept.
 * Cuisines when defined otherwise type as the fallback
 */
export const getConcept = ({
  cuisines,
  businessType,
}: {
  cuisines?: string | null;
  businessType?: string | null;
}): string => {
  businessType = businessType?.toUpperCase() === 'OTHER' ? '' : businessType;

  return toTitleCase(cuisines || businessType || '');
};

/**
 * Add's apostrophe s to the end of the business name if warranted
 */
export const getBusinessPossessiveName = (businessName: string) => {
  const name = businessName.trim();
  return _.endsWith(name, `'s`) ? `${name} ` : `${name}'s `;
};
